import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import AdminTable from "../components/AdminTable";
import AdminForm from "../components/AdminForm";
import {
  fetchBlocks,
  createBlock,
  updateBlock,
  deleteBlock,
  fetchPapers,
  reorderBlocks,
} from "../api/adminApi";
import { theme } from "../theme";
import { useToast } from "../context/ToastContext";
import { useConfirm } from "../context/ConfirmContext";
import Button from "../components/Button";

const Toolbar = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  margin-bottom: 20px;
  flex-wrap: wrap;
`;

const FilterSelect = styled.select`
  padding: 10px;
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radius.md}px;
  font-size: 14px;
  background: ${theme.colors.surface};
  color: ${theme.colors.text};
  font-family: ${theme.fonts.body};
`;

const SearchInput = styled.input`
  padding: 10px;
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radius.md}px;
  font-size: 14px;
  flex: 1;
  max-width: 600px;
  font-family: ${theme.fonts.body};
  color: ${theme.colors.text};
  background: ${theme.colors.surface};

  &:focus {
    outline: none;
    border-color: ${theme.colors.accent};
  }

  &::placeholder {
    color: ${theme.colors.textSecondary};
  }
`;

const Loading = styled.p`
  color: ${theme.colors.textSecondary};
  font-family: ${theme.fonts.body};
`;

const BlocksAdmin = () => {
  const navigate = useNavigate();
  const [blocks, setBlocks] = useState([]);
  const [papers, setPapers] = useState([]);
  const [filterPaperId, setFilterPaperId] = useState("");
  const [searchTitle, setSearchTitle] = useState("");
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editingBlock, setEditingBlock] = useState(null);
  const [saving, setSaving] = useState(false);
  const toast = useToast();
  const confirm = useConfirm();

  const loadBlocks = useCallback(
    async (paperId) => {
      try {
        setLoading(true);
        const data = await fetchBlocks(paperId || null);
        setBlocks(data);
      } catch (err) {
        toast.error("Ошибка загрузки данных");
      } finally {
        setLoading(false);
      }
    },
    [toast],
  );

  const getPaperName = (paperId) => {
    return papers?.find((p) => p.id === paperId)?.name;
  };

  const columns = [
    { Header: "Узор", accessor: "title" },
    {
      Header: "Цвет",
      accessor: "color",
      Cell: (row) =>
        row.color ? (
          <span
            style={{
              display: "inline-block",
              width: 20,
              height: 20,
              background: row.color,
              border: "1px solid #ddd",
              verticalAlign: "middle",
            }}
          />
        ) : (
          "—"
        ),
    },
    {
      Header: "Тип бумаги",
      accessor: "paper_id",
      Cell: (row) => getPaperName(row.paper_id),
    },
    {
      Header: "Порядок",
      accessor: "display_order",
      Cell: (row) =>
        row.display_order !== undefined && row.display_order !== null
          ? row.display_order + 1
          : "—",
    },
  ];

  const fields = (papers) => [
    {
      name: "paperId",
      label: "Тип бумаги",
      type: "select",
      required: true,
      options: papers.map((p) => ({ value: p.id, label: p.name })),
    },
    {
      name: "title",
      label: "Узор",
      type: "text",
      required: true,
      placeholder: "Введите название",
    },
  ];

  const loadPapers = async () => {
    try {
      const data = await fetchPapers();
      setPapers(data);
    } catch (err) {
      console.error("Failed to load papers:", err);
    }
  };

  useEffect(() => {
    loadPapers();
  }, []);

  useEffect(() => {
    loadBlocks(filterPaperId);
  }, [filterPaperId, loadBlocks]);

  const handleSave = async (values) => {
    setSaving(true);
    try {
      const data = {
        paperId: Number(values.paperId),
        title: values.title,
        original: values.original,
        text: values.text,
        color: values.color,
      };

      if (editingBlock) {
        await updateBlock(editingBlock.id, data);
        toast.success("Узор обновлён");
      } else {
        const newBlock = await createBlock(data);
        navigate(`/opredelitel/admin/blocks/${newBlock.id}`);
        toast.success("Узор добавлен");
      }
      setFormOpen(false);
      setEditingBlock(null);
      loadBlocks();
    } catch (err) {
      toast.error("Ошибка сохранения");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (block) => {
    navigate(`/opredelitel/admin/blocks/${block.id}`);
  };

  const handleDelete = async (block) => {
    const confirmed = await confirm({
      title: "Удалить блок?",
      message: `Блок "${block.title}" будет удалён.`,
    });
    if (!confirmed) return;
    try {
      await deleteBlock(block.id);
      toast.success("Блок удалён");
      loadBlocks();
    } catch (err) {
      toast.error("Ошибка удаления");
    }
  };

  const handleAdd = () => {
    setEditingBlock(null);
    setFormOpen(true);
  };

  const isReorderable = filterPaperId !== "" && searchTitle.trim() === "";

  const handleReorder = async (updatedBlocks) => {
    setBlocks(updatedBlocks);
    const blockOrders = updatedBlocks.map((b) => ({
      blockId: b.id,
      displayOrder: b.display_order,
    }));
    try {
      await reorderBlocks(blockOrders);
      toast.success("Порядок обновлён");
    } catch (err) {
      toast.error("Ошибка обновления порядка");
      loadBlocks();
    }
  };

  if (loading) return <Loading>Загрузка...</Loading>;

  const filteredBlocks = blocks?.filter((block) =>
    block.title.toLowerCase().includes(searchTitle.toLowerCase()),
  );

  return (
    <div>
      <Toolbar>
        <FilterSelect
          value={filterPaperId}
          onChange={(e) => setFilterPaperId(e.target.value)}
        >
          <option value="">Все</option>
          {papers.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </FilterSelect>
        <SearchInput
          type="text"
          placeholder="Поиск по заголовку..."
          value={searchTitle}
          onChange={(e) => setSearchTitle(e.target.value)}
        />
        {filterPaperId && (
          <Button $variant="success" onClick={handleAdd}>
            + Добавить
          </Button>
        )}
      </Toolbar>
      <AdminTable
        columns={columns}
        data={filteredBlocks}
        onEdit={handleEdit}
        onDelete={handleDelete}
        isReorderable={isReorderable}
        onReorder={handleReorder}
      />
      <AdminForm
        isOpen={formOpen}
        onClose={() => {
          setFormOpen(false);
          setEditingBlock(null);
        }}
        onSave={handleSave}
        fields={fields(papers)}
        isLoading={saving}
      />
    </div>
  );
};

export default BlocksAdmin;
